import { push, replace } from "react-router-redux";
import { delay } from "redux-saga";
import { all, call, put, select, spawn } from "redux-saga/effects";

import { selectors as configSelectors, types as configTypes } from "reducers/config";
import { selectors as i18nSelectors, types as i18nTypes } from "reducers/i18n";
import { actions as notificationActions } from "reducers/notification";
import { globalTypes } from "reducers/types/global";

import { MAX_FAILED_TIMES } from "constants.js";
import * as configUtil from "util/config";
import accounts from "sagas/accounts";
import addLimitRequest from "sagas/addLimitRequest";
import administrationAdvanced from "sagas/administration/advanced";
import administrationTicket from "sagas/administration/common/administrationTicket";
import administrationGroups from "sagas/administration/groups";
import administrationMedium from "sagas/administration/medium";
import restrictions from "sagas/administration/restrictions";
import administrationSimple from "sagas/administration/simple";
import administrationUsers from "sagas/administration/users";
import administrationUsersInvite from "sagas/administration/usersInvite";
import bankSelector from "sagas/bankSelector";
import campaigns from "sagas/campaigns";
import cedip from "sagas/cedip";
import checks from "sagas/checks";
import corporateCard from "sagas/corporateCard";
import communication from "sagas/communication";
import communications from "sagas/communications";
import communicationTrays from "sagas/communicationTrays";
import config from "sagas/config";
import debin from "sagas/debin";
import deposits from "sagas/deposits";
import desktop from "sagas/desktop";
import directDebit from "sagas/directDebit";
import echeqRule from "sagas/echeqRule";
import echeqMassManagement from "sagas/echeqMassManagement";
import echeqRules from "sagas/echeqRules";
import ecomex from "sagas/ecomex";
import ecomexIframe from "sagas/ecomexIframe";
import enrollment from "sagas/enrollment";
import files from "sagas/files";
import financing from "sagas/financing";
import fingerprint from "sagas/fingerprint";
import form from "sagas/form";
import formFields from "sagas/formFields";
import frequentDestination from "sagas/frequentDestination";
import fundcorp from "sagas/fundcorp";
import holidays from "sagas/holidays";
import i18n from "sagas/i18n";
import login from "sagas/login";
import massPayments from "sagas/massPayments";
import metrocorp from "sagas/metrocorp";
import multipleTransfers from "sagas/multiple-transfers";
import onboarding from "sagas/onboarding";
import paymentsAFIP from "sagas/paymentsAFIP";
import pointsOfInterest from "sagas/pointsOfInterest";
import posicionConsolidada from "sagas/posicionConsolidada";
import products from "sagas/products";
import pushNotifications from "sagas/pushNotifications";
import recoveryPassword from "sagas/recoveryPassword";
import servicePayments from "sagas/servicePayments";
import session from "sagas/session";
import settings from "sagas/settings";
import status from "sagas/status";
import template from "sagas/template";
import thirdPayment from "sagas/thirdPayment";
import token from "sagas/token";
import transactionLines from "sagas/transactionLines";
import transactions from "sagas/transactions";
import transfer from "sagas/transfer";
import weather from "sagas/weather";
import widgets from "sagas/widgets";
import currencyExchange from "sagas/currencyExchange";
import * as I18n from "util/i18n";

const sagas = [
    ...accounts,
    ...campaigns,
    ...communications,
    ...communication,
    ...communicationTrays,
    ...config,
    ...desktop,
    ...enrollment,
    ...fingerprint,
    ...form,
    ...i18n,
    ...onboarding,
    ...products,
    ...pushNotifications,
    ...recoveryPassword,
    ...status,
    ...session,
    ...settings,
    ...template,
    ...widgets,
    ...transactions,
    ...bankSelector,
    ...login,
    ...weather,
    ...administrationAdvanced,
    ...administrationGroups,
    ...administrationMedium,
    ...administrationSimple,
    ...administrationTicket,
    ...administrationUsers,
    ...administrationUsersInvite,
    ...files,
    ...formFields,
    ...pointsOfInterest,
    ...massPayments,
    ...transactionLines,
    ...multipleTransfers,
    ...transfer,
    ...debin,
    ...restrictions,
    ...frequentDestination,
    ...checks,
    ...corporateCard,
    ...deposits,
    ...token,
    ...thirdPayment,
    ...servicePayments,
    ...financing,
    ...paymentsAFIP,
    ...metrocorp,
    ...ecomex,
    ...posicionConsolidada,
    ...fundcorp,
    ...holidays,
    ...cedip,
    ...ecomexIframe,
    ...echeqRule,
    ...echeqMassManagement,
    ...echeqRules,
    ...directDebit,
    ...addLimitRequest,
    ...currencyExchange,
];

const actionsTransfer = ["SEND_EXTERNAL_TRANSFER_REQUEST", "SEND_INTERNAL_TRANSFER_REQUEST"];

// Con usuario recordado el step1 dispara el auto-login y redirige a step2, por lo que el aviso
// debe seguir visible en ambos pasos.
const SESSION_ERROR_SCOPES = ["login", "loginStep2"];

const getSagaName = (saga) => saga?.FORK?.args?.[1]?.name || "anonymous saga";

export default function* rootSaga() {
    yield all(
        sagas.map((saga) =>
            spawn(function* listenErrors() {
                let isSyncError = false;
                const resetSyncError = () => {
                    isSyncError = false;
                };
                let httpError = false;
                while (true) {
                    httpError = false;
                    isSyncError = true;
                    try {
                        setTimeout(resetSyncError);

                        yield call(function* execSaga() {
                            yield saga;
                        });
                        // eslint-disable-next-line no-console
                        console.error(
                            "Unexpected root saga termination. " +
                                "The root sagas are supposed to be sagas that live during the whole app lifetime!",
                            saga,
                        );
                    } catch (error) {
                        httpError = typeof error.httpError !== "undefined";
                        if (!httpError && isSyncError) {
                            const sagaName = getSagaName(saga);
                            // eslint-disable-next-line no-console
                            console.error(`[Saga startup error] ${sagaName}`, error);
                            throw new Error(
                                `${sagaName} was terminated because it threw an exception on startup: ${error.message}`,
                            );
                        }
                        yield call(handleError, error, saga);
                    }

                    if (!httpError) {
                        // Para evitar que fallas infinitas bloqueen el browser...
                        // eslint-disable-next-line no-console
                        console.error(getSagaName(saga), " will be restarted after 1 second");
                        yield call(delay, 1000);
                    }
                }
            }),
        ),
    );
}

export function* handleError(error, saga) {
    let errorControlled = false;

    if (error.data) {
        switch (error.data.code) {
            // Add known error codes as new cases for avoid general error message

            // User is blocked
            case "COR019E":
                yield put({ type: globalTypes.CLEAN_UP });
                break;
            case "API040E":
                errorControlled = true;
                yield put(
                    notificationActions.showNotification(I18n.get("products.operation.NoDispose"), "error", [
                        "desktop",
                    ]),
                );
                yield put(replace("/desktop"));
                break;
            // Attempt to sign an expired transaction
            case "COR108E":
                errorControlled = true;
                yield put(
                    notificationActions.showNotification(I18n.get("transaction.expired.signAttempt"), "error", [
                        "transactions",
                    ]),
                );
                yield put(replace("/transactions/list"));
                break;
            default:
                break;
        }

        if (!errorControlled) {
            // eslint-disable-next-line no-console
            console.error("[API Error Handler]:1", error.data, error.status);

            yield put(
                push({
                    pathname: "/error",
                    code: error.data.code,
                    message: error.data.message,
                }),
            );
        }
    } else if (error.response && error.response.status === 401) {
        // eslint-disable-next-line no-console
        console.error("[API Error Handler]:2", error.response);
        // The request was made and the server responded, but with a status code outside of 2xx
        yield put({ type: globalTypes.CLEAN_UP });

        switch (error.response.data.code) {
            case "API004W":
                yield put(
                    notificationActions.showNotification(I18n.get("session.expired"), "error", SESSION_ERROR_SCOPES),
                );
                break;

            case "API010W":
                yield put(
                    notificationActions.showNotification(
                        I18n.get("session.loggedinOtherDevice"),
                        "error",
                        SESSION_ERROR_SCOPES,
                    ),
                );
                break;

            case "API007E":
            case undefined: // The 401 sent by the Apigateway / Authorization server doesn't send a data code
                yield put(replace("/"));
                yield put(
                    notificationActions.showNotification(I18n.get("session.expired"), "error", SESSION_ERROR_SCOPES),
                );
                break;

            default:
                break;
        }
    } else if (error.request) {
        // eslint-disable-next-line no-console
        console.error("[API Error Handler]:3", error.request);
        const { FORK } = saga;
        const actionType = FORK?.args ? FORK?.args[0]?.split("/")[1] : "";
        const isTransfer = actionsTransfer.includes(actionType);
        // The request was made but no response was received
        const timesConfigFailed = yield select(configSelectors.getTimesFailed);
        const timesI18nFailed = yield select(i18nSelectors.getTimesFailed);

        if (timesConfigFailed >= MAX_FAILED_TIMES || timesI18nFailed >= MAX_FAILED_TIMES) {
            yield put(push({ pathname: "/serverError" }));
        } else {
            const lang = yield select(i18nSelectors.getLang) || configUtil.get("frontend.i18n.default.lang", "es");
            yield put({ type: configTypes.RESET_SAGAS_UPDATE, lang });
            yield put({ type: i18nTypes.RESET_SAGAS_UPDATE, lang });
            yield put(
                push({
                    pathname: "/error",
                    code: isTransfer ? "API528E" : "CLI999E",
                }),
            );
        }
    } else {
        // eslint-disable-next-line no-console
        console.error("[API Error Handler]:4", error.message);
        const { FORK } = saga;
        const actionType = FORK?.args ? FORK?.args[0]?.split("/")[1] : "";
        const isTransfer = actionsTransfer.includes(actionType);
        // Something happened in setting up the request that triggered an Error
        yield put(
            push({
                pathname: "/error",
                code: isTransfer ? "API528E" : "CLI999E",
            }),
        );
    }
}
